import { ThemeColorName } from '@examdojo/styles';
import type { ActionDefinition } from './widget-action.model';

// ==============================================================================
// UI CONTROL SYSTEM (The bindings)
// Maps external UI elements to internal Actions.
// ==============================================================================

/** Visual variants for slider controls */
export type WidgetSliderVariant = 'default' | 'minimal';

/** Control types supported by the widget system */
export const WIDGET_CONTROL_TYPE = {
  Button: 'button',
  ButtonGroup: 'button-group',
  Slider: 'slider',
  SliderGroup: 'slider-group',
  MathInput: 'math-input',
  DataTable: 'data-table',
  CanvasSelection: 'canvas-selection',
} as const;

export type WidgetControlType = (typeof WIDGET_CONTROL_TYPE)[keyof typeof WIDGET_CONTROL_TYPE];

/** Overlay positions for controls (matches label position naming) */
export type WidgetControlOverlayPosition = 'top-left' | 'top' | 'top-right' | 'bottom-left' | 'bottom' | 'bottom-right';

/** Panel positions for controls rendered outside the canvas (e.g., data tables) */
export type WidgetControlPanelPosition = 'panel-left' | 'panel-right' | 'panel-top' | 'panel-bottom';

/** Compound table positions: panel side + alignment in one value */
export type WidgetTablePosition =
  | 'right'
  | 'right-center'
  | 'right-bottom'
  | 'left'
  | 'left-center'
  | 'left-bottom'
  | 'top'
  | 'top-center'
  | 'top-right'
  | 'bottom'
  | 'bottom-center'
  | 'bottom-right';

/** Extract which panel a table position maps to */
export function getTablePanel(position: WidgetTablePosition | undefined): 'left' | 'right' | 'top' | 'bottom' {
  const pos = position ?? 'right';
  if (pos.startsWith('right')) {
    return 'right';
  }
  if (pos.startsWith('left')) {
    return 'left';
  }
  if (pos.startsWith('top')) {
    return 'top';
  }
  return 'bottom';
}

/** Extract CSS flex alignment value from a table position */
export function getTableAlignCss(position: WidgetTablePosition | undefined): string {
  const pos = position ?? 'right';
  if (pos.endsWith('-center')) {
    return 'center';
  }
  if (pos.endsWith('-bottom') || pos.endsWith('-right')) {
    return 'flex-end';
  }
  return 'flex-start';
}

/** Where controls can be rendered */
export type WidgetControlPosition = WidgetControlOverlayPosition | 'slider-footer' | WidgetControlPanelPosition;

/** Base control properties shared by all control types */
export interface WidgetControlBase<TType extends WidgetControlType = WidgetControlType> {
  /** Control type discriminator */
  type: TType;
  /** Unique ID for DOM keys */
  id: string;
  /** Human-readable label */
  label: string;
  /** Where to render this control */
  position?: WidgetControlPosition;
}

/**
 * Button control - triggers an action on click
 * Variants: text-only (no icon), icon-only (icon + showLabel:false), text+icon (icon + showLabel:true)
 */
export interface WidgetButtonControl extends WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['Button']> {
  /** FontAwesome icon name (e.g. 'magnifying-glass-plus') */
  icon?: string;
  /** Whether to show the label text. Default: true if no icon, false if icon present */
  showLabel?: boolean;
  /** The Action to fire when clicked */
  trigger: ActionDefinition;
  /** Optional group name — buttons sharing the same group render as a segment bar with active state */
  group?: string;
}

/** Button group control - renders as a segment (grouped related buttons) */
export interface WidgetButtonGroupControl extends WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['ButtonGroup']> {
  /** Buttons in this group */
  buttons: Array<Omit<WidgetButtonControl, 'type' | 'position'>>;
}

/**
 * Slider control - numeric range input with optional editable input field.
 *
 * The `param` field uses dot-notation paths to target parameters:
 * - Simple: "amplitude" (for widgets without elements)
 * - Element.param: "f.a" (targets element 'f', param 'a' - graphpad style)
 *
 * The UPDATE_PARAM action is auto-generated from the param path.
 */
export interface WidgetSliderControl extends WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['Slider']> {
  /**
   * Parameter path using dot notation.
   * - graphpad: "f.a" → element 'f', param 'a'
   * - simple widget: "amplitude" → param 'amplitude'
   */
  param: string;
  /** [min, max, step?] - Range and optional step for the slider */
  range: [number, number, number?];
  /** Default value (defaults to min if not provided) */
  defaultValue?: number;
  /** Whether to show an editable input box for direct value entry (default: false) */
  editable?: boolean;
  /** Color for the slider handle and trace */
  color?: ThemeColorName;
  /** Visual variant: 'default' or 'minimal' (compact track, smaller handle) */
  variant?: WidgetSliderVariant;
}

/** Slider group control - multiple sliders stacked in a styled container */
export interface WidgetSliderGroupControl extends WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['SliderGroup']> {
  /** Sliders in this group */
  sliders: Array<Omit<WidgetSliderControl, 'type' | 'position'>>;
}

/** Helper to extract min from slider range */
export function getSliderMin(control: Pick<WidgetSliderControl, 'range'>): number {
  return control.range?.[0] ?? 0;
}

/** Helper to extract max from slider range */
export function getSliderMax(control: Pick<WidgetSliderControl, 'range'>): number {
  return control.range?.[1] ?? 100;
}

/** Helper to extract step from slider range (defaults to 1) */
export function getSliderStep(control: Pick<WidgetSliderControl, 'range'>): number {
  return control.range?.[2] ?? 1;
}

/**
 * Math input control - input field only (no slider) for numeric values.
 *
 * Like sliders, uses dot-notation param paths for UPDATE_PARAM action.
 */
export interface WidgetMathInputControl extends WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['MathInput']> {
  /**
   * Parameter path using dot notation.
   * - graphpad: "f.a" → element 'f', param 'a'
   * - simple widget: "amplitude" → param 'amplitude'
   */
  param: string;
  /** Default value */
  defaultValue?: number;
}

// ==============================================================================
// DATA TABLE CONTROL
// ==============================================================================

/** Column definition for a data table */
export interface WidgetDataTableColumn {
  /** Column identifier, maps to row values keys */
  key: string;
  /** Display header text */
  header: string;
  /** Column alignment. Default: 'center' */
  align?: 'left' | 'center' | 'right';
}

/** Row definition for a data table */
export interface WidgetDataTableRow {
  /** Column key → cell display value */
  values: Record<string, string | number>;
  /** Action to dispatch when this row is hovered */
  onHover?: ActionDefinition;
  /** Action to dispatch when hover leaves this row */
  onLeave?: ActionDefinition;
}

/**
 * Data table control — displays tabular data alongside the canvas.
 * Row hover dispatches actions (same ActionDefinition as button triggers).
 */
export interface WidgetDataTableControl
  extends Omit<WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['DataTable']>, 'position'> {
  /** Column definitions */
  columns: WidgetDataTableColumn[];
  /** Row data with optional hover actions */
  rows: WidgetDataTableRow[];
  /** Whether to show the header row. Default: true */
  showHeader?: boolean;
  /** Table position relative to the canvas. Default: 'right'
   *  Right panel: 'right', 'right-center', 'right-bottom'
   *  Left panel:  'left',  'left-center',  'left-bottom'
   *  Top panel:   'top',   'top-center',   'top-right'
   *  Bottom panel: 'bottom', 'bottom-center', 'bottom-right' */
  position?: WidgetTablePosition;
}

// ==============================================================================
// CANVAS SELECTION CONTROL
// ==============================================================================

/**
 * Canvas selection control — an interactive radio button rendered directly on the JSXGraph board.
 *
 * Positioned via `location: { x, y }` where each axis is a literal number or
 * a string expression / port path (e.g. `'t1.edge.AB.midpoint.x'`).
 * - With `group`: radio-button behavior (clicking one deselects others in the same group)
 * - Without `group`: standalone toggle (click toggles selected/unselected)
 */
export interface WidgetCanvasSelectionControl
  extends WidgetControlBase<(typeof WIDGET_CONTROL_TYPE)['CanvasSelection']> {
  /** Where to place this control on the board (literal number or string expression per axis) */
  location: { x: number | string; y: number | string };
  /** Action to fire when this control is selected */
  trigger: ActionDefinition;
  /** Action to fire when this control is deselected (standalone toggles only) */
  triggerOff?: ActionDefinition;
  /** Optional group name for mutual exclusivity (radio-button behavior) */
  group?: string;
  /** Whether this control is initially selected */
  selected?: boolean;
}

/** Union of all control types */
export type WidgetControl =
  | WidgetButtonControl
  | WidgetButtonGroupControl
  | WidgetSliderControl
  | WidgetSliderGroupControl
  | WidgetMathInputControl
  | WidgetDataTableControl
  | WidgetCanvasSelectionControl;
